// Data, styles and helpers for the bus-ticket reservations console
// (reservations-console, reservation-modals).

export const TODAY = "2026-09-28";

export interface TripItem {
  id: string;
  from: string;
  to: string;
  cls: "CLASSIQUE" | "VIP";
  date: string;
  heure: string;
  bus: string;
  // Seats already sold outside this console.
  taken: number;
  prix: number;
  cap: number;
}

export type BoardStatus = "En attente" | "Vu" | "Embarqué" | "Absent" | "Annulé";
export type PayStatus = "Payé" | "Non payé" | "Partiel" | "Remboursé";

export interface ReservationItem {
  id: string;
  pre: string;
  ticket: string;
  createdBy: string;
  createdAt: string;
  source: string;
  agence: string;
  name: string;
  phone: string;
  cni: string;
  // Trip id, "" when no bus is assigned (open trip).
  trip: string;
  seat: number | null;
  from: string;
  to: string;
  date: string;
  heure: string;
  cls: "CLASSIQUE" | "VIP";
  pay: PayStatus;
  refOM: string;
  refMomo: string;
  canal: string;
  montant: number;
  paid: number;
  confirmed: boolean;
  confirmedBy: string;
  confirmedAt: string;
  board: BoardStatus;
  bag: boolean;
  linkSent: boolean;
  audit: [string, string][];
  note: string;
}

/* ---------------------------------- Dates --------------------------------- */

const AG = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const MO = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
export const pad = (n: number) => ("0" + n).slice(-2);
const dObj = (iso: string) => new Date(iso.slice(0, 10) + "T12:00:00");
const cap1 = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
export const fDate = (iso: string) => {
  if (!iso) return "—";
  const d = dObj(iso);
  return `${AG[d.getDay()]} ${d.getDate()} ${MO[d.getMonth()]} ${d.getFullYear()}`;
};
export const fDateCap = (iso: string) => {
  const d = dObj(iso);
  return `${cap1(AG[d.getDay()])}, ${d.getDate()} ${cap1(MO[d.getMonth()])} ${d.getFullYear()}`;
};
export const fShort = (iso: string) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : "");
export const fDash = (iso: string) => (iso ? `${iso.slice(8, 10)}-${iso.slice(5, 7)}-${iso.slice(0, 4)}` : "—");
export const addDays = (iso: string, n: number) => {
  const d = dObj(iso);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/* ---------------------------------- Misc ---------------------------------- */

export const money = (n: number) =>
  Math.round(n)
    .toLocaleString("fr-FR")
    .replace(/[  ,]/g, " ") + " FCFA";
export const num = (v: string) => parseFloat(String(v || "").replace(/\s/g, "").replace(",", ".")) || 0;
export const norm = (s: string) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
export const generateTicketCode = () => "TIK" + Math.random().toString(16).slice(2, 10).toUpperCase();

/* --------------------------------- Trips ---------------------------------- */

export const CITY_CODE: Record<string, string> = {
  DOUALA: "DLA",
  "YAOUNDÉ": "YDE",
  NKONGSAMBA: "NKSB",
  BAFIA: "BAF",
  LOUM: "LOUM",
  MANJO: "MNJ",
  BAFOUSSAM: "BFM",
  KRIBI: "KRB",
};
export const CITIES = Object.keys(CITY_CODE);

const T = (id: string, from: string, to: string, cls: TripItem["cls"], date: string, heure: string, bus: string, taken: number, prix: number): TripItem => ({
  id,
  from,
  to,
  cls,
  date,
  heure,
  bus,
  taken,
  prix,
  cap: cls === "VIP" ? 30 : 56,
});

export const TRIPS: TripItem[] = [
  T("t0", "DOUALA", "BAFIA", "VIP", "2026-09-26", "07:30", "LT 783 MZ (A)", 27, 6000),
  T("t15", "YAOUNDÉ", "LOUM", "CLASSIQUE", "2026-09-24", "08:00", "LT 876 OC (K33993)", 49, 6500),
  T("t1", "DOUALA", "NKONGSAMBA", "CLASSIQUE", "2026-09-28", "12:00", "LT 774 MP", 28, 3000),
  T("t2", "LOUM", "YAOUNDÉ", "CLASSIQUE", "2026-09-28", "19:30", "LT 877 OC (K33997)", 26, 6500),
  T("t3", "NKONGSAMBA", "DOUALA", "CLASSIQUE", "2026-09-28", "12:00", "LT 775 MP", 42, 3000),
  T("t4", "DOUALA", "NKONGSAMBA", "CLASSIQUE", "2026-09-28", "13:00", "LT 868 OC (K33980)", 44, 3000),
  T("t17", "NKONGSAMBA", "DOUALA", "CLASSIQUE", "2026-09-28", "13:00", "LT 422 NN", 56, 3000),
  T("t6", "MANJO", "YAOUNDÉ", "CLASSIQUE", "2026-09-28", "14:00", "LT 874 OC (K33988)", 26, 6000),
  T("t5", "DOUALA", "YAOUNDÉ", "VIP", "2026-09-28", "14:00", "CH 200 365", 22, 10000),
  T("t8", "DOUALA", "YAOUNDÉ", "CLASSIQUE", "2026-09-29", "06:00", "AD 513 AX", 51, 5000),
  T("t16", "YAOUNDÉ", "NKONGSAMBA", "CLASSIQUE", "2026-09-29", "06:30", "LT 124 OB (CH104467)", 18, 5500),
  T("t7", "DOUALA", "BAFIA", "CLASSIQUE", "2026-09-29", "07:30", "LT 783 MZ", 30, 6000),
  T("t11", "DOUALA", "BAFOUSSAM", "CLASSIQUE", "2026-09-29", "08:00", "LT 640 PL", 33, 5000),
  T("t9", "YAOUNDÉ", "DOUALA", "VIP", "2026-09-29", "10:00", "CE 204 118", 12, 10000),
  T("t10", "NKONGSAMBA", "YAOUNDÉ", "CLASSIQUE", "2026-09-29", "11:30", "LT 123 OB (CH104470)", 20, 5500),
  T("t13", "DOUALA", "KRIBI", "CLASSIQUE", "2026-09-30", "09:00", "SW 318 KR", 9, 3500),
  T("t18", "DOUALA", "YAOUNDÉ", "VIP", "2026-09-30", "18:00", "CH 200 366", 5, 10000),
];

export const tripById = (id: string) => TRIPS.find((t) => t.id === id);

// Copies the trip's route and schedule onto the reservation.
export const hydrateReservation = (r: ReservationItem): ReservationItem => {
  const t = tripById(r.trip);
  return t ? { ...r, from: t.from, to: t.to, date: t.date, heure: t.heure, cls: t.cls } : r;
};

export type TripRange = "today" | "7j" | "retrans";

export const RANGE_OPTIONS: [TripRange, string][] = [
  ["today", "Aujourd'hui"],
  ["7j", "7 prochains jours"],
  ["retrans", "Saisir une retranscription"],
];

export function tripsFor(range: TripRange, q: string, cls: string): TripItem[] {
  const lim = addDays(TODAY, 7);
  const qq = norm(q.trim());
  return TRIPS.filter(
    (t) =>
      (range === "today" ? t.date === TODAY : range === "7j" ? t.date >= TODAY && t.date <= lim : t.date < TODAY) &&
      (cls === "TOUS" || t.cls === cls) &&
      (!qq || norm(`${t.from} ${t.to} ${t.bus}`).includes(qq))
  ).sort((a, b) => (a.date + a.heure).localeCompare(b.date + b.heure));
}

/* ---------------------------------- Seats --------------------------------- */

// Deterministic "already sold" seats for a trip, avoiding seats held in the console.
function seeded(id: string, n: number, cap: number, avoid: Set<number>) {
  let h = 7;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) % 9973;
  const s = new Set<number>();
  let x = h;
  let i = 0;
  while (s.size < n && i < cap * 6) {
    x = (x * 73 + 41) % 9973;
    const k = (x % cap) + 1;
    if (!avoid.has(k)) s.add(k);
    i++;
  }
  for (let k = 1; s.size < n && k <= cap; k++) if (!avoid.has(k)) s.add(k);
  return s;
}

// Occupied seats on a trip. `excl` = reservations whose seats should count as free (the one being edited).
export function takenSet(t: TripItem, list: ReservationItem[], excl: string[] = []) {
  const own = new Set<number>();
  const res = new Set<number>();
  list.forEach((r) => {
    if (r.trip === t.id && r.seat && r.pay !== "Remboursé" && r.board !== "Annulé") {
      own.add(r.seat);
      if (!excl.includes(r.id)) res.add(r.seat);
    }
  });
  const s = seeded(t.id, Math.max(0, t.taken - own.size), t.cap, own);
  res.forEach((n) => s.add(n));
  return s;
}

export const remainingSeats = (t: TripItem, list: ReservationItem[], excl: string[] = []) =>
  Math.max(0, t.cap - takenSet(t, list, excl).size);

// Seat grid rows; null = aisle / empty slot. VIP 2+1, CLASSIQUE 2+2.
export function seatLayout(t: TripItem): (number | null)[][] {
  const [left, right] = t.cls === "VIP" ? [2, 1] : [2, 2];
  const per = left + right;
  const rows: (number | null)[][] = [];
  for (let i = 0; i < Math.ceil(t.cap / per); i++) {
    const cells: (number | null)[] = [];
    for (let j = 0; j < per; j++) {
      if (j === left) cells.push(null);
      const n = i * per + j + 1;
      cells.push(n > t.cap ? null : n);
    }
    rows.push(cells);
  }
  return rows;
}

/* --------------------------------- Styles --------------------------------- */

export const PAY_STYLE: Record<PayStatus, { bg: string; fg: string }> = {
  "Payé": { bg: "#d1fae5", fg: "#047857" },
  "Non payé": { bg: "#fee2e2", fg: "#b91c1c" },
  "Partiel": { bg: "#fef3c7", fg: "#b45309" },
  "Remboursé": { bg: "#e5e7eb", fg: "#4b5563" },
};

export const BOARD_STYLE: Record<BoardStatus, { bg: string; fg: string; dot: string }> = {
  "En attente": { bg: "#fff7ed", fg: "#c2410c", dot: "#fb923c" },
  "Vu": { bg: "#ede9fe", fg: "#5b21b6", dot: "#7c3aed" },
  "Embarqué": { bg: "#d1fae5", fg: "#047857", dot: "#10b981" },
  "Absent": { bg: "#fee2e2", fg: "#b91c1c", dot: "#ef4444" },
  "Annulé": { bg: "#f1f5f9", fg: "#64748b", dot: "#94a3b8" },
};

export const CLS_STYLE: Record<string, { bg: string; fg: string }> = {
  VIP: { bg: "#6d28d9", fg: "#ffffff" },
  CLASSIQUE: { bg: "#e7e5ee", fg: "#3f3a52" },
};

/* ---------------------------------- Seed ---------------------------------- */

const R = (o: Partial<ReservationItem> & Pick<ReservationItem, "id" | "createdBy" | "createdAt" | "source" | "name" | "phone">): ReservationItem => ({
  pre: "",
  ticket: "",
  agence: "",
  cni: "",
  trip: "",
  seat: null,
  from: "",
  to: "",
  date: "",
  heure: "",
  cls: "CLASSIQUE",
  pay: "Non payé",
  refOM: "",
  refMomo: "",
  canal: "—",
  montant: 0,
  paid: 0,
  confirmed: false,
  confirmedBy: "",
  confirmedAt: "",
  board: "En attente",
  bag: false,
  linkSent: false,
  audit: [],
  note: "",
  ...o,
});

export const RAW_RESERVATIONS: ReservationItem[] = [
  R({ id: "r1", pre: "2609-DLA-28002", createdBy: "MICHELLE", createdAt: "2026-09-28T08:41", source: "PASSENGER_PUBLIC_PORTAL", agence: "Agence DEIDO", name: "MICHELLE", phone: "237696539776", cni: "101165588", from: "DOUALA", to: "NKONGSAMBA", date: "2026-09-28", heure: "14:30", audit: [["28/09 08:41", "Pré-réservation créée sur le portail passager"], ["28/09 08:41", "Départ souhaité 14:30, aucun bus affecté"]] }),
  R({ id: "r2", pre: "2609-DLA-28001", createdBy: "MICHELLE", createdAt: "2026-09-28T08:37", source: "PASSENGER_PUBLIC_PORTAL", name: "MICHELLE", phone: "237696539776", cni: "101168588", from: "DOUALA", to: "NKONGSAMBA", date: "2026-09-28", heure: "14:30", audit: [["28/09 08:37", "Pré-réservation créée sur le portail passager"]] }),
  R({ id: "r3", createdBy: "BAPPA", createdAt: "2026-09-24T22:03", source: "PASSENGER_ONLINE", name: "LISETTE FELICITE EDIMO", phone: "+237657516685", trip: "t7", montant: 6000, audit: [["24/09 22:03", "Réservation en ligne"]] }),
  R({ id: "r4", createdBy: "BAPPA", createdAt: "2026-09-24T22:04", source: "PASSENGER_ONLINE", name: "LISETTE FELICITE EDIMO", phone: "+237657516685", trip: "t7", montant: 6000, audit: [["24/09 22:04", "Réservation en ligne"]] }),
  R({ id: "r5", createdBy: "BAPPA", createdAt: "2026-09-24T22:06", source: "PASSENGER_ONLINE", name: "LISETTE FELICITE EDIMO", phone: "+237657516685", trip: "t7", montant: 6000, audit: [["24/09 22:06", "Réservation en ligne"]] }),
  R({ id: "r6", createdBy: "BAPPA", createdAt: "2026-09-24T22:10", source: "PASSENGER_ONLINE", name: "RIKAM CLEMANTINE", phone: "+237675011614", trip: "t7", montant: 6000, audit: [["24/09 22:10", "Réservation en ligne"]] }),
  R({ id: "r7", ticket: "TIKB81FEA62", createdBy: "BAPPA", createdAt: "2026-09-24T22:03", source: "PASSENGER_ONLINE", name: "RIKAM CLEMANTINE", phone: "+237675011614", trip: "t0", seat: 24, pay: "Payé", refOM: "MP240926.2211.B8120", canal: "Orange Money", montant: 6000, paid: 6000, confirmed: true, confirmedBy: "BAPPA", confirmedAt: "2026-09-24T22:15", board: "Vu", audit: [["24/09 22:03", "Réservation en ligne"], ["24/09 22:11", "Paiement Orange Money reçu"], ["24/09 22:15", "Billet émis par BAPPA · siège 24"], ["26/09 07:02", "Billet contrôlé au quai"]] }),
  R({ id: "r8", pre: "2609-YDE-23001", ticket: "TIK63156EF6", createdBy: "FELICIA ETAPE MUNGE EPSE NGOME", createdAt: "2026-09-23T07:10", source: "PASSENGER_PUBLIC_PORTAL", agence: "Agence BERTOUA", name: "FELICIA ETAPE MUNGE EPSE NGOME", phone: "+237677368037", cni: "101301737", trip: "t15", seat: 9, pay: "Payé", refMomo: "MM230926.0712.556102", canal: "MTN MoMo", montant: 6500, paid: 6500, confirmed: true, confirmedBy: "yasmine", confirmedAt: "2026-09-23T07:30", board: "Vu", audit: [["23/09 07:10", "Pré-réservation portail"], ["23/09 07:12", "Paiement MoMo reçu"], ["23/09 07:30", "Billet émis par yasmine · siège 9"]] }),
  R({ id: "r9", pre: "2609-NKSB-27002", createdBy: "MOFFO ARIELLE", createdAt: "2026-09-27T18:20", source: "PASSENGER_PUBLIC_PORTAL", name: "MOFFO ARIELLE", phone: "237674845749", trip: "t3", seat: 17, pay: "Payé", refOM: "MP270926.1822.K4410", canal: "Orange Money", montant: 3000, paid: 3000, audit: [["27/09 18:20", "Pré-réservation portail · siège 17"], ["27/09 18:22", "Référence Orange Money transmise par la cliente"]] }),
  R({ id: "r10", pre: "2609-YDE-25004", ticket: "TIK9A04C2D1", createdBy: "DONGMO HUGUES CANTIN", createdAt: "2026-09-25T15:02", source: "PASSENGER_PUBLIC_PORTAL", agence: "Agence MVAN", name: "DONGMO HUGUES CANTIN", phone: "237682061365", cni: "100982214", trip: "t16", seat: 15, pay: "Payé", refMomo: "MM250926.1505.771203", canal: "MTN MoMo", montant: 5500, paid: 5500, confirmed: true, confirmedBy: "BAPPA", confirmedAt: "2026-09-25T15:20", audit: [["25/09 15:02", "Pré-réservation portail"], ["25/09 15:20", "Billet émis par BAPPA · siège 15"]] }),
  R({ id: "r11", ticket: "TIK5F21B7A0", createdBy: "yasmine", createdAt: "2026-09-28T09:05", source: "GUICHET", agence: "Agence AKWA", name: "NGONO BRIGITTE", phone: "+237699412208", cni: "112204561", trip: "t5", seat: 7, pay: "Payé", canal: "Espèces", montant: 10000, paid: 10000, confirmed: true, confirmedBy: "yasmine", confirmedAt: "2026-09-28T09:05", audit: [["28/09 09:05", "Vente au guichet Akwa · espèces · siège 7"]] }),
  R({ id: "r12", pre: "2809-DLA-28006", createdBy: "BAPPA", createdAt: "2026-09-28T07:50", source: "WHATSAPP", name: "TCHOUPO ARNAUD", phone: "+237655902417", trip: "t11", seat: 31, pay: "Partiel", refOM: "MP280926.0755.P2231", canal: "Orange Money", montant: 5000, paid: 2000, note: "Solde de 3 000 FCFA à régler au guichet avant le départ.", audit: [["28/09 07:50", "Réservation WhatsApp par BAPPA"], ["28/09 07:55", "Acompte Orange Money 2 000 FCFA"]] }),
  R({ id: "r13", ticket: "TIK7C11E9F3", createdBy: "yasmine", createdAt: "2026-09-26T11:20", source: "GUICHET", agence: "Agence AKWA", name: "ESSOMBA JEAN-PAUL", phone: "+237677204331", trip: "t8", seat: 3, pay: "Remboursé", canal: "Espèces", montant: 5000, paid: 0, confirmed: true, confirmedBy: "yasmine", confirmedAt: "2026-09-26T11:20", board: "Annulé", audit: [["26/09 11:20", "Vente au guichet · siège 3"], ["27/09 16:05", "Annulation à la demande du client · remboursé"]] }),
  R({ id: "r14", pre: "2809-DLA-28004", createdBy: "NDZANA SANDRINE", createdAt: "2026-09-28T06:58", source: "PASSENGER_ONLINE", name: "NDZANA SANDRINE", phone: "+237690775310", trip: "t13", montant: 3500, linkSent: true, audit: [["28/09 06:58", "Réservation en ligne"], ["28/09 07:00", "Lien de paiement envoyé par SMS"]] }),
  R({ id: "r15", ticket: "TIK2E88D014", createdBy: "BAPPA", createdAt: "2026-09-27T16:40", source: "AGENT", agence: "Agence DEIDO", name: "ABENA MARC", phone: "+237677120984", cni: "110457723", trip: "t1", seat: 12, pay: "Payé", refMomo: "MM270926.1641.330981", canal: "MTN MoMo", montant: 3000, paid: 3000, confirmed: true, confirmedBy: "BAPPA", confirmedAt: "2026-09-27T16:45", board: "Embarqué", audit: [["27/09 16:40", "Réservation agent"], ["27/09 16:45", "Billet émis · siège 12"], ["28/09 11:48", "Embarqué"]] }),
  R({ id: "r16", ticket: "TIK4B90AA17", createdBy: "KAMGA PAUL", createdAt: "2026-09-27T20:12", source: "PASSENGER_ONLINE", name: "KAMGA PAUL", phone: "+237694330871", cni: "108812344", trip: "t9", seat: 4, pay: "Payé", refOM: "MP270926.2014.Q0012", canal: "Orange Money", montant: 10000, paid: 10000, confirmed: true, confirmedBy: "yasmine", confirmedAt: "2026-09-27T20:30", audit: [["27/09 20:12", "Réservation en ligne"], ["27/09 20:30", "Billet émis par yasmine · siège 4"]] }),
].map(hydrateReservation);
